import { Checkbox } from "antd";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MobileStepper } from "@mui/material";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import { EyeTwoTone, EyeInvisibleOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import API_BASE from "../../config/api";

const REGISTRATION_TOKEN_STORAGE_KEY = "smartlend_registration_token";

function isSmartlendHost() {
  return (
    typeof window !== "undefined" &&
    window.location.hostname.endsWith("smart-lend.com")
  );
}

function persistRegistrationToken(token) {
  if (!token) return;

  const cookieOptions = {
    expires: 365,
    path: "/",
    sameSite: "Lax",
    secure: window.location.protocol === "https:",
  };

  Cookies.set("token", token, cookieOptions);

  if (isSmartlendHost()) {
    Cookies.set("token", token, {
      ...cookieOptions,
      domain: ".smart-lend.com",
    });
  }

  try {
    window.localStorage.setItem(REGISTRATION_TOKEN_STORAGE_KEY, token);
    window.sessionStorage.setItem(REGISTRATION_TOKEN_STORAGE_KEY, token);
  } catch (error) {
    // Browser storage can be unavailable in private modes. The cookie still works.
  }
}

function clearRegistrationToken() {
  Cookies.remove("token", { path: "/" });

  if (isSmartlendHost()) {
    Cookies.remove("token", { path: "/", domain: ".smart-lend.com" });
  }

  try {
    window.localStorage.removeItem(REGISTRATION_TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(REGISTRATION_TOKEN_STORAGE_KEY);
  } catch (error) {
    // Ignore storage cleanup failures.
  }
}

function getStoredRegistrationToken() {
  try {
    return (
      Cookies.get("token") ||
      window.sessionStorage.getItem(REGISTRATION_TOKEN_STORAGE_KEY) ||
      window.localStorage.getItem(REGISTRATION_TOKEN_STORAGE_KEY) ||
      ""
    );
  } catch (error) {
    return Cookies.get("token") || "";
  }
}

function ConfirmPassword() {
  const [password, setPassword] = useState("");
  const [password_confirmation, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [checkbox, setCheckbox] = useState(false);
  const history = useHistory();
  const [visible, setVisible] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [screenWidth, setScreenWidth] = useState("1920");
  const [isLoadingScreen, setIsLoadingScreen] = useState(false);
  const [authToken, setAuthToken] = useState(() => getStoredRegistrationToken());

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token") || queryParams.get("access_token");

    if (token) {
      persistRegistrationToken(token);
      setAuthToken(token);

      const { pathname } = location;
      const newUrl = pathname + location.hash;
      history.replace(newUrl);
      return;
    }

    const storedToken = getStoredRegistrationToken();
    if (storedToken) {
      setAuthToken(storedToken);
    }
  }, [location, history]);

  // const token = sessionStorage.getItem("token");
  const getPasswordValidationError = () => {
    if (!password || !password_confirmation) {
      return "Please enter and confirm your password.";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (password !== password_confirmation) {
      return "Passwords do not match.";
    }
    return "";
  };

  const handleConfirmPassword = async (event) => {
    event.preventDefault();

    const validationError = getPasswordValidationError();
    if (validationError) {
      setError(validationError);
      return;
    }

    const token = authToken || getStoredRegistrationToken();
    if (!token) {
      setError(
        "We could not authenticate your verification session. Please reopen the latest verification email link."
      );
      return;
    }

    setIsLoadingScreen(true);
    setError("");

    try {
      const dataTopush = {
        password: password,
        password_confirmation: password_confirmation,
      };
      const response = await fetch(
        `${API_BASE}/register/password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(dataTopush),
        }
      );

      const json = await response.json();

      if (response.status === 200) {
        persistRegistrationToken(token);
        history.push("/auth/confirm-email");
      } else if (response.status === 401) {
        clearRegistrationToken();
        setAuthToken("");
        setError(
          "Your verification session has expired. Please reopen the latest verification email link."
        );
      } else {
        const apiMessage =
          json?.message ||
          json?.error ||
          json?.errors?.password?.[0] ||
          json?.errors?.password_confirmation?.[0];
        setError(apiMessage || "Unable to set password. Please try again.");
      }
    } catch (error) {
      console.error(error);
      setError("Unable to set password. Please try again.");
    } finally {
      setIsLoadingScreen(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    // Call handleResize initially to set the initial screen width
    handleResize();

    // Add event listener for resize
    window.addEventListener("resize", handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {isLoadingScreen ? (
        <div
          style={{
            background: "rgba(256,256,256,0.4)",
            height: window.innerHeight,
            width: window.innerWidth,
            position: "fixed",
            top: 0,
            left: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress size={"5rem"} />
        </div>
      ) : null}
      {screenWidth > 1200 ? (
        <React.Fragment>
          <div className="auth-header auth-header-password-step">
            <h3 className="auth-header-title-password">
              Lock in your account credentials.
            </h3>
            <p className="auth-header-subtitle-password">
              Set a password after email verification. This keeps the account
              ready before phone confirmation.
            </p>
          </div>
          <div className="auth-body">
            <form className="auth-form-validation">
              <div className="input-field">
                <label htmlFor="password" className="input-label">
                  Password
                </label>
                <input
                  type={visible ? "text" : "password"}
                  // name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  // id="password"
                  className="input-control"
                  placeholder=" Password"
                  autoComplete="off"
                  required
                  suffix={
                    <span style={{ position: "absolute", right: 5, bottom: 8 }}>
                      {visible ? (
                        <EyeTwoTone onClick={() => setVisible(false)} />
                      ) : (
                        <EyeInvisibleOutlined
                          onClick={() => setVisible(true)}
                        />
                      )}
                    </span>
                  }
                />
              </div>
              <div className="input-field">
                <label htmlFor="password" className="input-label">
                  Re-enter Password
                </label>
                <input
                  type={visible ? "text" : "password"}
                  // name="password"
                  // id="confirmPassword"
                  value={password_confirmation}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-control"
                  placeholder=" Re-enter Password"
                  autoComplete="off"
                  required
                  suffix={
                    <span style={{ position: "absolute", right: 5, bottom: 8 }}>
                      {visible2 ? (
                        <EyeTwoTone onClick={() => setVisible2(false)} />
                      ) : (
                        <EyeInvisibleOutlined
                          onClick={() => setVisible2(true)}
                        />
                      )}
                    </span>
                  }
                />
              </div>
              <div className="auth-policy-row">
                <Checkbox
                  checked={checkbox}
                  onChange={(e) => setCheckbox(e.target.checked)}
                >
                  I agree to the{" "}
                </Checkbox>
                <Link
                  to={"/auth/contact-us"}
                  className="link-text-center-password"
                >
                  privacy policy and terms.
                </Link>
              </div>
              <div className="btn-position">
                <button
                  type="submit"
                  className={checkbox ? "btn-submit" : "disabled-button"}
                  onClick={handleConfirmPassword}
                  style={{ width: "100%" }}
                  disabled={!checkbox}
                >
                  Confirm Password
                </button>
                {error && <p style={{ color: "red" }}>{error}</p>}
              </div>
            </form>
            <div className="auth-stepper-row">
              <MobileStepper
                variant="dots"
                steps={6}
                position="static"
                activeStep={2}
                sx={{ maxWidth: 400, flexGrow: 1 }}
              />
            </div>
          </div>
        </React.Fragment>
      ) : (
        <div>
          <div
            style={{
              background:
                "transparent linear-gradient(180deg, #FFFFFFB3 0%, #FFFFFF00 100%) 0% 0% no-repeat padding-box",
              width: "95%",
              marginLeft: "10px",
            }}
          >
            <div className="auth-header auth-header-password-step">
              <h3 className="auth-header-title-password">
                Lock in your account credentials.
              </h3>
              <p className="auth-header-subtitle-password">
                Set a password after email verification. This keeps the account
                ready before phone confirmation.
              </p>
            </div>
            <div className="auth-body">
              <form className="auth-form-validation">
                <div className="input-field">
                  <label htmlFor="password" className="input-label">
                    Password
                  </label>
                  <input
                    type={visible ? "text" : "password"}
                    // name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    // id="password"
                    className="input-control"
                    placeholder=" Password"
                    autoComplete="off"
                    required
                    suffix={
                      <span
                        style={{ position: "absolute", right: 5, bottom: 8 }}
                      >
                        {visible ? (
                          <EyeTwoTone onClick={() => setVisible(false)} />
                        ) : (
                          <EyeInvisibleOutlined
                            onClick={() => setVisible(true)}
                          />
                        )}
                      </span>
                    }
                  />
                </div>
                <div className="input-field">
                  <label htmlFor="password" className="input-label">
                    Re-enter Password
                  </label>
                  <input
                    type={visible ? "text" : "password"}
                    // name="password"
                    // id="confirmPassword"
                    value={password_confirmation}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-control"
                    placeholder=" Re-enter Password"
                    autoComplete="off"
                    required
                    suffix={
                      <span
                        style={{ position: "absolute", right: 5, bottom: 8 }}
                      >
                        {visible2 ? (
                          <EyeTwoTone onClick={() => setVisible2(false)} />
                        ) : (
                          <EyeInvisibleOutlined
                            onClick={() => setVisible2(true)}
                          />
                        )}
                      </span>
                    }
                  />
                </div>
                <div className="auth-policy-row">
                  <Checkbox
                    checked={checkbox}
                    onChange={(e) => setCheckbox(e.target.checked)}
                  >
                    I agree to the{" "}
                  </Checkbox>
                  <Link
                    to={"/auth/contact-us"}
                    className="link-text-center-password"
                  >
                    privacy policy and terms.
                  </Link>
                </div>
                <div className="btn-position">
                  <button
                    type="submit"
                    className={checkbox ? "btn-submit" : "disabled-button"}
                    onClick={handleConfirmPassword}
                    disabled={!checkbox}
                  >
                    Confirm Password
                  </button>
                  {error && <p style={{ color: "red" }}>{error}</p>}
                </div>
              </form>
              <div className="auth-stepper-row">
                <MobileStepper
                  variant="dots"
                  steps={6}
                  position="static"
                  activeStep={2}
                  sx={{ maxWidth: 400 }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ConfirmPassword;
